// BannerOferta — promotional countdown banner for the Academy page.
//   • Reads end date from REACT_APP_OFFER_END (ISO 8601 UTC).
//   • Disappears automatically when the countdown hits zero.
//   • Hidden for users who already have isPro = true.
//   • Dismissible per session (sessionStorage).
//   • Three languages with proper Slovak plural rules.
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { getBannerOfertaI18n, labelDias, labelHoras } from "../../i18n/banner-oferta";

const DEFAULT_END_AT = "2026-08-02T23:59:59Z";

const computeRemaining = (endDate) => {
  const diff = endDate.getTime() - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
  return {
    d: Math.floor(diff / 86_400_000),
    h: Math.floor((diff % 86_400_000) / 3_600_000),
    m: Math.floor((diff % 3_600_000) / 60_000),
    s: Math.floor((diff % 60_000) / 1_000),
    expired: false,
  };
};

const STORAGE_KEY = "rebel_banner_oferta_dismissed_v1";

// Resolve the API base at runtime so production (rebelbtc.com) uses
// same-origin requests via the Vercel rewrite, instead of the build-time
// REACT_APP_BACKEND_URL which would point at the preview backend.
import { API_BASE } from "../../lib/api";
import { Loader2 } from "lucide-react";
import RBLPaymentButton from "../pro/RBLPaymentButton";

// Stripe checkout starter — same call the simulator uses successfully in prod.
const BannerCheckoutButton = ({ label }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API_BASE}/api/payments/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          plan: "pro_lifetime",
          origin_url: window.location.origin,
        }),
      });
      const data = await res.clone().json().catch(() => ({}));
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      // If checkout creation failed and we got no URL, send the user to login
      // (most common reason) so they can authenticate then retry.
      if (!token) {
        window.location.href = "/login";
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[BannerOferta] checkout returned no url:", data);
      setBusy(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BannerOferta] checkout failed:", err);
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="academy-banner-cta"
      className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-black shadow-[0_4px_16px_rgba(245,166,35,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(245,166,35,0.55)] disabled:opacity-60 disabled:cursor-wait"
    >
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {label}
    </button>
  );
};

export const BannerOferta = () => {
  const { isPro } = useAuth();
  const { lang } = useLanguage();
  const T = getBannerOfertaI18n(lang);

  // Resolve once per mount (string env var is stable).
  const endDate = useMemo(
    () => new Date(process.env.REACT_APP_OFFER_END || DEFAULT_END_AT),
    []
  );

  const [time, setTime] = useState(() => computeRemaining(endDate));
  const [dismissed, setDismissed] = useState(false);

  // Read sessionStorage on mount (avoid SSR mismatch on tooling that pre-renders).
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY)) setDismissed(true);
    } catch {
      // sessionStorage may be unavailable (private mode) — fall through.
    }
  }, []);

  // 1-second tick. Stops automatically when expired.
  useEffect(() => {
    if (time.expired) return undefined;
    const id = setInterval(() => setTime(computeRemaining(endDate)), 1000);
    return () => clearInterval(id);
  }, [endDate, time.expired]);

  // Don't render: user already PRO, dismissed, or offer expired.
  if (isPro || dismissed || time.expired) return null;

  const dismiss = () => {
    try { window.sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const cells = [
    { val: time.d, label: labelDias(time.d, lang) },
    { val: time.h, label: labelHoras(time.h, lang) },
    { val: time.m, label: T.minutos },
    { val: time.s, label: T.segundos },
  ];

  return (
    <section
      role="banner"
      aria-label={T.labelOferta}
      data-testid="academy-banner-oferta"
      className="relative mx-auto mt-6 mb-2 max-w-6xl overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-[#0d0e1c] via-[#0a1628] to-[#0d0e1c] shadow-[0_0_32px_rgba(245,166,35,0.12),0_4px_24px_rgba(0,0,0,0.4)] animate-banner-in"
    >
      {/* Animated shimmer top line */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] animate-banner-shimmer"
        style={{
          background:
            "linear-gradient(90deg, transparent, #f5a623, #ff6b35, #f5a623, transparent)",
          backgroundSize: "200% 100%",
        }}
        aria-hidden
      />

      {/* Dismiss */}
      <button
        type="button"
        onClick={dismiss}
        aria-label={T.cerrar}
        data-testid="academy-banner-dismiss"
        className="absolute right-3 top-2 z-10 rounded-full p-1.5 text-white/35 transition-colors hover:bg-white/[0.06] hover:text-white"
      >
        <span className="block h-4 w-4 text-center text-base leading-4">×</span>
      </button>

      <div className="flex flex-col items-stretch gap-6 px-5 py-5 sm:px-7 sm:py-6 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
        {/* ── Left: badges + price + description ───────────── */}
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-amber-400/40 bg-amber-400/[0.15] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
              {T.labelOferta}
            </span>
            <span className="inline-flex items-center rounded-full border border-emerald-400/35 bg-emerald-400/[0.1] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
              {T.labelVitalicio}
            </span>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-mono font-black leading-none tracking-tight text-amber-400 text-[clamp(2rem,4vw,2.75rem)]">
              €39.90
            </span>
            <span className="max-w-md text-xs leading-snug text-white/55 sm:text-sm">
              {T.descripcion}
            </span>
          </div>

          <p className="mt-2 text-[11px] leading-relaxed text-white/35">
            {T.subDescripcion}
          </p>
        </div>

        {/* ── Center: countdown ───────────────────────────── */}
        <div
          className="flex flex-col items-center gap-2 lg:shrink-0"
          data-testid="academy-banner-countdown"
        >
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
            {T.quedan}
          </span>
          <div className="flex items-end gap-1.5 sm:gap-2">
            {cells.map((cell, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span className="-mt-3 self-center pb-3 font-mono text-xl text-amber-400/40">:</span>
                )}
                <div className="flex flex-col items-center">
                  <span
                    className="inline-flex min-w-[46px] items-center justify-center rounded-md border border-amber-400/30 bg-black/40 px-2 py-1.5 font-mono text-[clamp(1.15rem,2.2vw,1.6rem)] font-black leading-none tracking-wider text-amber-300 sm:min-w-[52px]"
                    data-testid={`countdown-cell-${i}`}
                  >
                    {String(cell.val).padStart(2, "0")}
                  </span>
                  <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-white/30">
                    {cell.label}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Right: CTA ──────────────────────────────────── */}
        <div className="flex flex-col items-stretch gap-2 lg:shrink-0 lg:min-w-[280px]">
          <BannerCheckoutButton label={T.cta} />
          <p className="text-center font-mono text-[9px] text-white/30">{T.garantia}</p>
          <RBLPaymentButton />
        </div>
      </div>
    </section>
  );
};

export default BannerOferta;
